import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import AdBanner from '@/components/AdBanner';
import { MapPin, Instagram, Calendar, MessageCircle, Settings, Grid3X3, Heart, Trash2, Star, Plus, Image } from 'lucide-react';
import ReviewSection from '@/components/ReviewSection';
import RequestAppointmentButton from '@/components/RequestAppointmentButton';
import RequestQuoteButton from '@/components/RequestQuoteButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { userId } = useParams();
  const { user: currentUser, token, updateUser } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [clientTattoos, setClientTattoos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editData, setEditData] = useState({});
  const [newTattoo, setNewTattoo] = useState({
    image_url: '',
    description: '',
    artist_name: ''
  });

  const isOwnProfile = currentUser?.id === userId;
  const isArtist = profile?.user_type === 'independent_artist' ||
    profile?.user_type === 'studio_artist' ||
    profile?.user_type === 'studio' ||
    profile?.user_type === 'artist';
  const isClient = profile?.user_type === 'client';

  useEffect(() => {
    fetchProfile();
    fetchArtworks();
    fetchClientTattoos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setProfile(response.data);
      setIsFollowing(currentUser?.following?.includes(userId) || false);
      if (isOwnProfile) {
        setEditData({
          name: response.data.name,
          bio: response.data.bio,
          location: response.data.location,
          instagram: response.data.instagram,
          phone: response.data.phone
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const fetchArtworks = async () => {
    try {
      const response = await axios.get(`${API}/artworks?artist_id=${userId}`);
      setArtworks(response.data);
    } catch (error) {
      console.error('Error fetching artworks:', error);
    }
  };

  const fetchClientTattoos = async () => {
    try {
      const response = await axios.get(`${API}/client-tattoos/${userId}`);
      setClientTattoos(response.data);
    } catch (error) {
      console.error('Error fetching client tattoos:', error);
    }
  };

  const handleUploadTattoo = async () => {
    if (!newTattoo.image_url) {
      toast.error(language === 'es' ? 'Añade una URL de imagen' : 'Add an image URL');
      return;
    }

    setUploading(true);
    try {
      const response = await axios.post(`${API}/client-tattoos`, newTattoo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientTattoos(prev => [response.data, ...prev]);
      setNewTattoo({ image_url: '', description: '', artist_name: '' });
      setUploadOpen(false);
      toast.success(language === 'es' ? '¡Tatuaje añadido!' : 'Tattoo added!');
    } catch (error) {
      console.error('Error uploading tattoo:', error);
      toast.error(language === 'es' ? 'Error al subir el tatuaje' : 'Error uploading tattoo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClientTattoo = async (tattooId) => {
    if (!window.confirm(language === 'es' ? '¿Seguro que quieres eliminar este tatuaje?' : 'Are you sure you want to delete this tattoo?')) return;
    try {
      await axios.delete(`${API}/client-tattoos/${tattooId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientTattoos(prev => prev.filter(t => t.id !== tattooId));
      toast.success(language === 'es' ? 'Tatuaje eliminado' : 'Tattoo deleted');
    } catch (error) {
      console.error('Error deleting tattoo:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const handleFollow = async () => {
    try {
      const response = await axios.post(`${API}/users/${userId}/follow`);
      setIsFollowing(response.data.following);
      setProfile(prev => ({
        ...prev,
        followers_count: prev.followers_count + (response.data.following ? 1 : -1)
      }));
    } catch (error) {
      console.error('Error following user:', error);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const response = await axios.put(`${API}/users/me`, editData);
      setProfile(response.data);
      updateUser(response.data);
      setEditOpen(false);
      toast.success('Perfil actualizado');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error al actualizar el perfil');
    }
  };

  const handleDeleteArtwork = async (artworkId) => {
    if (!window.confirm('¿Seguro que quieres eliminar este trabajo?')) return;
    try {
      await axios.delete(`${API}/artworks/${artworkId}`);
      setArtworks(prev => prev.filter(a => a.id !== artworkId));
      toast.success('Trabajo eliminado');
    } catch (error) {
      console.error('Error deleting artwork:', error);
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-zinc-700 border-t-white animate-spin"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="font-['Cinzel'] text-2xl font-bold text-white mb-4">Usuario no encontrado</h2>
        <Link to="/feed" className="text-zinc-500 hover:text-white transition-colors">
          Volver al feed
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="profile-container">
      {/* Cover Image */}
      <div
        className="h-48 md:h-64 bg-zinc-900 bg-cover bg-center"
        style={{ backgroundImage: profile.cover_image ? `url(${profile.cover_image})` : undefined }}
      />

      {/* Profile Info - responsive padding */}
      <div className="px-3 sm:px-4 md:px-8">
        <div className="flex flex-col md:flex-row gap-6 -mt-16 md:-mt-20">
          <Avatar className="h-32 w-32 md:h-40 md:w-40 border-4 border-zinc-950">
            <AvatarImage src={profile.avatar} alt={profile.name} />
            <AvatarFallback className="bg-zinc-800 text-zinc-400 text-4xl">
              {profile.name?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 pt-4 md:pt-20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="font-['Cinzel'] text-3xl font-bold text-white" data-testid="profile-name">
                  {profile.name}
                </h1>
                <p className="text-sm uppercase tracking-wider text-zinc-500 mt-1">
                  {isArtist
                    ? (profile.user_type === 'independent_artist'
                      ? (language === 'es' ? 'Tatuador Independiente' : 'Independent Artist')
                      : profile.user_type === 'studio_artist'
                        ? (language === 'es' ? 'Tatuador de Estudio' : 'Studio Artist')
                        : profile.user_type === 'studio'
                          ? (language === 'es' ? 'Estudio' : 'Studio')
                          : (language === 'es' ? 'Tatuador' : 'Artist'))
                    : (language === 'es' ? 'Cliente' : 'Client')}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 sm:gap-3">
                {isOwnProfile ? (
                  <>
                    {/* Upload button for clients */}
                    {isClient && (
                      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="border-zinc-700 text-zinc-300 hover:border-zinc-300 hover:text-white bg-transparent uppercase tracking-widest text-xs"
                            data-testid="upload-tattoo-btn"
                          >
                            <Plus size={16} className="mr-2" />
                            {language === 'es' ? 'Añadir Tatuaje' : 'Add Tattoo'}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-900 border-zinc-800">
                          <DialogHeader>
                            <DialogTitle className="font-['Cinzel'] text-xl">
                              {language === 'es' ? 'Añadir Tatuaje' : 'Add Tattoo'}
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                                {language === 'es' ? 'URL de la imagen' : 'Image URL'}
                              </Label>
                              <Input
                                value={newTattoo.image_url}
                                onChange={(e) => setNewTattoo(prev => ({ ...prev, image_url: e.target.value }))}
                                className="bg-transparent border-zinc-800 mt-2"
                                placeholder="https://..."
                                data-testid="tattoo-url-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                                {language === 'es' ? 'Descripción' : 'Description'}
                              </Label>
                              <Textarea
                                value={newTattoo.description}
                                onChange={(e) => setNewTattoo(prev => ({ ...prev, description: e.target.value }))}
                                className="bg-transparent border-zinc-800 mt-2"
                                placeholder={language === 'es' ? 'Cuéntanos sobre este tatuaje...' : 'Tell us about this tattoo...'}
                                data-testid="tattoo-description-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                                {language === 'es' ? 'Nombre del artista (opcional)' : 'Artist name (optional)'}
                              </Label>
                              <Input
                                value={newTattoo.artist_name}
                                onChange={(e) => setNewTattoo(prev => ({ ...prev, artist_name: e.target.value }))}
                                className="bg-transparent border-zinc-800 mt-2"
                                placeholder={language === 'es' ? '¿Quién te lo hizo?' : 'Who made it?'}
                                data-testid="tattoo-artist-input"
                              />
                            </div>
                            <Button
                              onClick={handleUploadTattoo}
                              disabled={uploading}
                              className="w-full bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold py-4"
                              data-testid="save-tattoo-btn"
                            >
                              {uploading
                                ? (language === 'es' ? 'Subiendo...' : 'Uploading...')
                                : (language === 'es' ? 'Guardar Tatuaje' : 'Save Tattoo')}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}

                    <Dialog open={editOpen} onOpenChange={setEditOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="border-zinc-700 text-zinc-300 hover:border-zinc-300 hover:text-white bg-transparent uppercase tracking-widest text-xs"
                          data-testid="edit-profile-btn"
                        >
                          <Settings size={16} className="mr-2" />
                          Editar Perfil
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-zinc-900 border-zinc-800">
                        <DialogHeader>
                          <DialogTitle className="font-['Cinzel'] text-xl">Editar Perfil</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500">Nombre</Label>
                            <Input
                              value={editData.name || ''}
                              onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                              className="bg-transparent border-zinc-800 mt-2"
                              data-testid="edit-name-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500">Bio</Label>
                            <Textarea
                              value={editData.bio || ''}
                              onChange={(e) => setEditData(prev => ({ ...prev, bio: e.target.value }))}
                              className="bg-transparent border-zinc-800 mt-2 min-h-[100px]"
                              data-testid="edit-bio-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500">Ubicación</Label>
                            <Input
                              value={editData.location || ''}
                              onChange={(e) => setEditData(prev => ({ ...prev, location: e.target.value }))}
                              className="bg-transparent border-zinc-800 mt-2"
                              data-testid="edit-location-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500">Instagram</Label>
                            <Input
                              value={editData.instagram || ''}
                              onChange={(e) => setEditData(prev => ({ ...prev, instagram: e.target.value }))}
                              className="bg-transparent border-zinc-800 mt-2"
                              placeholder="@usuario"
                              data-testid="edit-instagram-input"
                            />
                          </div>
                          <Button
                            onClick={handleUpdateProfile}
                            className="w-full bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold py-4"
                            data-testid="save-profile-btn"
                          >
                            Guardar Cambios
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </>
                ) : (
                  <>
                    {isArtist && (
                      <>
                        <RequestAppointmentButton artistId={userId} artistName={profile.name} />
                        <RequestQuoteButton artistId={userId} artistName={profile.name} />
                      </>
                    )}
                    <Button
                      onClick={handleFollow}
                      className={`uppercase tracking-widest text-xs font-bold px-6 ${isFollowing
                        ? 'bg-transparent border border-zinc-700 text-zinc-300 hover:border-zinc-300'
                        : 'bg-zinc-100 text-zinc-950 hover:bg-white'
                        }`}
                      data-testid="follow-btn"
                    >
                      {isFollowing ? 'Siguiendo' : 'Seguir'}
                    </Button>
                    <Button
                      onClick={() => navigate(`/messages/${userId}`)}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:border-zinc-300 hover:text-white bg-transparent"
                      data-testid="message-btn"
                    >
                      <MessageCircle size={18} />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 sm:gap-8 mt-6">
              <div>
                <p className="font-mono text-2xl text-white">{profile.artworks_count || 0}</p>
                <p className="text-xs uppercase tracking-wider text-zinc-500">Trabajos</p>
              </div>
              <div>
                <p className="font-mono text-2xl text-white">{profile.followers_count || 0}</p>
                <p className="text-xs uppercase tracking-wider text-zinc-500">Seguidores</p>
              </div>
              <div>
                <p className="font-mono text-2xl text-white">{profile.following_count || 0}</p>
                <p className="text-xs uppercase tracking-wider text-zinc-500">Siguiendo</p>
              </div>
              {profile.user_type === 'artist' && profile.reviews_count > 0 && (
                <div>
                  <p className="font-mono text-2xl text-white flex items-center gap-1">
                    {profile.average_rating || 0}
                    <Star size={16} className="fill-yellow-500 text-yellow-500" />
                  </p>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">{profile.reviews_count} Reseñas</p>
                </div>
              )}
            </div>

            {/* Bio & Info */}
            {profile.bio && (
              <p className="text-zinc-400 mt-6 max-w-2xl">{profile.bio}</p>
            )}

            <div className="flex flex-wrap gap-4 mt-4">
              {profile.location && (
                <span className="flex items-center gap-2 text-sm text-zinc-500">
                  <MapPin size={14} />
                  {profile.location}
                </span>
              )}
              {profile.instagram && (
                <a
                  href={`https://instagram.com/${profile.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors"
                >
                  <Instagram size={14} />
                  {profile.instagram}
                </a>
              )}
            </div>

            {/* Styles */}
            {profile.styles?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {profile.styles.map((style) => (
                  <span key={style} className="px-3 py-1 text-xs uppercase tracking-wider bg-zinc-800 text-zinc-400">
                    {style}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Content Tabs */}
        <Tabs defaultValue="gallery" className="mt-12">
          <TabsList className="bg-transparent border-b border-zinc-800 w-full justify-start rounded-none h-auto pb-0">
            <TabsTrigger
              value="gallery"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-white data-[state=active]:bg-transparent px-6 py-4 text-xs uppercase tracking-wider"
              data-testid="gallery-tab"
            >
              <Grid3X3 size={16} className="mr-2" />
              Galería
            </TabsTrigger>
            {isArtist && (
              <TabsTrigger
                value="appointments"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-white data-[state=active]:bg-transparent px-6 py-4 text-xs uppercase tracking-wider"
                data-testid="appointments-tab"
              >
                <Calendar size={16} className="mr-2" />
                {language === 'es' ? 'Citas' : 'Appointments'}
              </TabsTrigger>
            )}
            {isClient && (
              <TabsTrigger
                value="my-tattoos"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-white data-[state=active]:bg-transparent px-6 py-4 text-xs uppercase tracking-wider"
                data-testid="my-tattoos-tab"
              >
                <Image size={16} className="mr-2" />
                {language === 'es' ? 'Mis Tatuajes' : 'My Tattoos'}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="gallery" className="mt-8">
            {isArtist ? (
              // Artist gallery
              artworks.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-zinc-500">
                    {isOwnProfile
                      ? (language === 'es' ? 'No has subido ningún trabajo aún' : "You haven't uploaded any work yet")
                      : (language === 'es' ? 'Este artista no tiene trabajos publicados' : 'This artist has no published work')}
                  </p>
                  {isOwnProfile && (
                    <Link
                      to="/upload"
                      className="inline-block mt-4 text-white hover:underline"
                    >
                      {language === 'es' ? 'Subir tu primer trabajo' : 'Upload your first work'}
                    </Link>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
                  {artworks.map((artwork) => (
                    <div
                      key={artwork.id}
                      className="relative group aspect-square bg-zinc-900 overflow-hidden"
                      data-testid={`profile-artwork-${artwork.id}`}
                    >
                      <img
                        src={artwork.image_url}
                        alt={artwork.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-6">
                        <span className="flex items-center gap-2 text-white">
                          <Heart size={18} />
                          {artwork.likes_count}
                        </span>
                        <span className="flex items-center gap-2 text-white">
                          <MessageCircle size={18} />
                          {artwork.comments_count}
                        </span>
                        {isOwnProfile && (
                          <button
                            onClick={() => handleDeleteArtwork(artwork.id)}
                            className="text-rose-400 hover:text-rose-300 transition-colors"
                            data-testid={`delete-artwork-${artwork.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              // Client - show their tattoo collection
              clientTattoos.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-zinc-500">
                    {isOwnProfile
                      ? (language === 'es' ? 'No has añadido ningún tatuaje a tu colección' : "You haven't added any tattoos to your collection")
                      : (language === 'es' ? 'Este usuario no ha compartido sus tatuajes' : "This user hasn't shared their tattoos")}
                  </p>
                  {isOwnProfile && (
                    <Button
                      onClick={() => setUploadOpen(true)}
                      className="mt-4 bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
                    >
                      {language === 'es' ? 'Añadir tu primer tatuaje' : 'Add your first tattoo'}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
                  {clientTattoos.map((tattoo) => (
                    <div
                      key={tattoo.id}
                      className="relative group aspect-square bg-zinc-900 overflow-hidden"
                      data-testid={`client-tattoo-${tattoo.id}`}
                    >
                      <img
                        src={tattoo.image_url}
                        alt={tattoo.description || 'Tattoo'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-4">
                        {tattoo.artist_name && (
                          <p className="text-white text-sm mb-2">
                            {language === 'es' ? 'Por' : 'By'}: {tattoo.artist_name}
                          </p>
                        )}
                        {tattoo.description && (
                          <p className="text-zinc-300 text-xs text-center line-clamp-2">
                            {tattoo.description}
                          </p>
                        )}
                        {isOwnProfile && (
                          <button
                            onClick={() => handleDeleteClientTattoo(tattoo.id)}
                            className="mt-2 text-rose-400 hover:text-rose-300 transition-colors"
                            data-testid={`delete-client-tattoo-${tattoo.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* Reviews Section - only for artists */}
            {isArtist && (
              <ReviewSection artistId={userId} artistName={profile.name} />
            )}
          </TabsContent>

          {/* My Tattoos tab for clients */}
          {isClient && (
            <TabsContent value="my-tattoos" className="mt-8">
              {clientTattoos.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-zinc-500">
                    {isOwnProfile
                      ? (language === 'es' ? 'Muestra tu colección de tatuajes aquí' : 'Show your tattoo collection here')
                      : (language === 'es' ? 'Este usuario no ha compartido sus tatuajes' : "This user hasn't shared their tattoos")}
                  </p>
                  {isOwnProfile && (
                    <Button
                      onClick={() => setUploadOpen(true)}
                      className="mt-4 bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
                    >
                      <Plus size={16} className="mr-2" />
                      {language === 'es' ? 'Añadir Tatuaje' : 'Add Tattoo'}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  {clientTattoos.map((tattoo) => (
                    <div
                      key={tattoo.id}
                      className="bg-zinc-900 border border-zinc-800 overflow-hidden"
                      data-testid={`my-tattoo-${tattoo.id}`}
                    >
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={tattoo.image_url}
                          alt={tattoo.description || 'Tattoo'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        {tattoo.artist_name && (
                          <p className="text-white text-sm font-medium">
                            {language === 'es' ? 'Por' : 'By'}: {tattoo.artist_name}
                          </p>
                        )}
                        {tattoo.description && (
                          <p className="text-zinc-500 text-sm mt-1 line-clamp-2">
                            {tattoo.description}
                          </p>
                        )}
                        {isOwnProfile && (
                          <button
                            onClick={() => handleDeleteClientTattoo(tattoo.id)}
                            className="mt-3 text-rose-400 hover:text-rose-300 transition-colors text-xs flex items-center gap-1"
                          >
                            <Trash2 size={14} />
                            {language === 'es' ? 'Eliminar' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          <TabsContent value="appointments" className="mt-8">
            <div className="text-center py-16">
              <p className="text-zinc-500">Gestiona tus citas desde la sección de Citas</p>
              <Link
                to="/appointments"
                className="inline-block mt-4 bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold px-8 py-4"
              >
                Ir a Citas
              </Link>
            </div>
          </TabsContent>
        </Tabs>

        {/* Ad banner after gallery */}
        {!isOwnProfile && (
          <div className="mt-8">
            <AdBanner slot="3497156484" format="horizontal" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
